/**
 * The seam between the UI and the backend (mock or real AWS).
 *
 * The UI only ever talks to these interfaces, so swapping the mock
 * implementation for the Cognito/S3-backed one is a single wiring change.
 */

import rolesArtifact from '../config/roles.json';

/**
 * Employee role groups (from Cognito `cognito:groups`). Suppliers have no
 * employee group. The role vocabulary and the role→area mapping are governed by
 * a single artifact (src/config/roles.json); the Python token trigger reads a
 * matching copy, so the two languages can't drift.
 *  - admin             → all areas (Connect + Control + Insights)
 *  - supplier-control  → Control + Insights
 *  - supplier-insights → Insights
 */
export type UserRole = 'admin' | 'supplier-control' | 'supplier-insights';

/** Top-level app areas a signed-in user can reach. */
export type AppArea = 'connect' | 'control' | 'insights';

/**
 * Version of the role→area Shared Kernel this build compiled in. Surfaced so it
 * can be compared against the token trigger's `kernel_version` to detect a
 * rollout where the two deployables disagree on the kernel.
 */
export const KERNEL_VERSION: string = rolesArtifact.version;

export interface SupplierUser {
    email: string;
    company: string;
    /**
     * Employee role groups. Empty/undefined for password suppliers (who only
     * reach the Connect area).
     */
    roles?: UserRole[];
    /**
     * This supplier's M2M OAuth client id for the ASN API, provisioned at
     * admin approval. Safe to display (it identifies the client, like a
     * username). The matching clientSecret is issued once at approval and is
     * never exposed to the app.
     */
    clientId?: string;
    /**
     * Profile photo URL from the identity provider (Midway `picture` claim).
     * Present for federated employees; undefined for password suppliers.
     */
    picture?: string;
    /**
     * True for federated (Midway SSO) employees, false/undefined for native
     * password suppliers. Drives the no-role fallback: an employee in none of
     * the role groups is denied access, while a supplier falls back to the
     * Connect (Supplier Gateway) area.
     */
    isEmployee?: boolean;
    /**
     * Brand role for a supplier login: 'MASTER' can self-serve adding teammates
     * (Team Members screen); 'MEMBER' cannot. Undefined for employees and for
     * logins with no role stamped (treated as a member). Read from the ID
     * token's `role` claim.
     */
    supplierRole?: 'MASTER' | 'MEMBER';
}

/**
 * Areas a user can access, derived from their roles. The mapping is data-driven
 * from the governed roles artifact (not a hand-maintained branch), so it stays
 * in step with the Python token trigger:
 *  - admin             → Connect + Control + Insights (everything)
 *  - supplier-control  → Control + Insights
 *  - supplier-insights → Insights
 *  - none (supplier)   → Connect only
 *  - none (employee)   → [] (access denied)
 *
 * The no-role fallback depends on WHO the user is (`isEmployee`): a native
 * supplier gets the Connect default, but a federated employee who is in none of
 * the role groups gets nothing — the caller renders an access-denied screen.
 */
export function areasFor(roles: UserRole[] | undefined, isEmployee = false): AppArea[] {
    const areaOrder = rolesArtifact.areas as AppArea[];
    const byName = new Map<string, string[]>(rolesArtifact.roles.map((r) => [r.name, r.areas]));
    const granted = new Set<string>((roles ?? []).flatMap((role) => byName.get(role) ?? []));
    if (granted.size === 0) {
        return isEmployee ? [] : (rolesArtifact.defaultAreas as AppArea[]);
    }
    return areaOrder.filter((area) => granted.has(area));
}

export interface SignUpParams {
    email: string;
    password: string;
    company: string;
    /** ISO 3166-1 alpha-2 country code (required at sign-up). */
    country: string;
    /** Optional contact person's name (stored in the standard `name` attribute). */
    contactName?: string;
    /** Optional contact phone in E.164 format (e.g. +14155550123). */
    phone?: string;
}

/** Counts for one integration type over a window. */
export interface IntegrationCounts {
    total: number;
    dataErrors: number;
    businessErrors: number;
}

/** Per-integration ASN metrics for a time window. */
export interface AsnMetrics {
    from: string;
    to: string;
    /**
     * The viewer identities the counts were summed over — the Cognito
     * identity-pool id (File Drop) and, when provisioned, the M2M clientId
     * (API/presigned). Informational; the UI reads `byIntegration`.
     */
    identityIds?: string[];
    /** Keyed by integration type: 'API' | 'PRESIGNED_URL' | 'FILE_DROP'. */
    byIntegration: Record<string, IntegrationCounts>;
}

/**
 * One failed ASN for the drill-down drawer. A submission carrying multiple ASNs
 * expands into one entry per ASN, so the drawer shows actual ASNs (not files).
 */
export interface AsnFailure {
    submissionId: string;
    /** The ASN number this row is for (blank if the payload failed to parse). */
    asnNumber: string;
    /** Integration source: 'API' | 'PRESIGNED_URL' | 'FILE_DROP'. */
    source: string;
    /** Server receipt time (ISO-8601). */
    receivedAt: string;
    /** Submitting login/client, when recorded. */
    supplierId: string;
    /** Human-readable error detail (joined validation errors or failure reason). */
    error: string;
}

/** A page of failed submissions for one source + bucket over a window. */
export interface AsnFailures {
    from: string;
    to: string;
    source: string;
    /** 'data' | 'business'. */
    bucket: string;
    failures: AsnFailure[];
    /** Cursor to fetch the next page, or null/undefined when exhausted. */
    nextCursor?: string | null;
}

/** Query for the failures drill-down; mirrors GET /v1/asn/failures. */
export interface AsnFailuresQuery {
    /** Integration source key ('API' | 'PRESIGNED_URL' | 'FILE_DROP'). */
    source: string;
    /** 'data' | 'business'. */
    bucket: 'data' | 'business';
    from?: string;
    to?: string;
    identityId?: string;
    limit?: number;
    /** Opaque cursor from a prior response's `nextCursor`. */
    cursor?: string;
}

export interface AsnMetricsService {
    /**
     * Per-integration ASN counts over [from, to] (ISO-8601). Omit the range for
     * the service default window. Always scoped server-side to the signed-in
     * viewer's token identity, so the caller cannot request another login.
     */
    getMetrics(from?: string, to?: string): Promise<AsnMetrics>;

    /**
     * Drill-down list of the actual failed submissions behind a count, for one
     * source + error bucket over the window. Paginated via `cursor`.
     */
    getFailures(query: AsnFailuresQuery): Promise<AsnFailures>;
}

/**
 * Raised when sign-in is rejected because the account exists but is not yet
 * approved by an admin (disabled after email confirmation).
 */
export class PendingApprovalError extends Error {
    constructor(message = 'Your account is awaiting approval.') {
        super(message);
        this.name = 'PendingApprovalError';
    }
}

/**
 * Result of a password sign-in: either fully signed in, or an email MFA code
 * is required before a session is issued.
 */
export type SignInResult =
    | { status: 'SIGNED_IN'; user: SupplierUser }
    | { status: 'EMAIL_MFA_REQUIRED' }
    // The account exists but its email isn't verified yet (Cognito returns the
    // CONFIRM_SIGN_UP step). The UI should route to the confirm-email screen.
    | { status: 'CONFIRMATION_REQUIRED' };

export interface AuthService {
    /**
     * Register a new supplier. Returns once the user pool has accepted the
     * sign-up and (for real Cognito) dispatched a verification email.
     */
    signUp(params: SignUpParams): Promise<void>;

    /**
     * Confirm a sign-up using the code emailed to the supplier.
     */
    confirmSignUp(email: string, code: string): Promise<void>;

    /**
     * Re-send the sign-up verification code to the supplier's email. Used from
     * the confirm screen when the first code is delayed, lost, or expired.
     */
    resendSignUpCode(email: string): Promise<void>;

    /**
     * Sign in with email + password. Resolves to a signed-in user or an MFA
     * challenge, or throws PendingApprovalError if the account isn't approved.
     */
    signIn(email: string, password: string): Promise<SignInResult>;

    /**
     * Complete an email-MFA sign-in with the code emailed to the supplier.
     * The email is used as a fallback for the returned user when Cognito
     * attributes are not yet readable post-MFA.
     */
    confirmSignIn(code: string, email?: string): Promise<SupplierUser>;

    /**
     * Re-issue an email MFA code by re-initiating sign-in. Used when the first
     * code is delayed or has expired. Expects to land back on the email-MFA
     * challenge; throws if the pool returns an unexpected step.
     */
    resendMfaCode(email: string, password: string): Promise<void>;

    /**
     * Begin self-service password reset: Cognito emails a verification code to
     * the account's email. Resolves once the code has been dispatched.
     */
    resetPassword(email: string): Promise<void>;

    /**
     * Complete a password reset with the emailed code and the new password.
     */
    confirmResetPassword(email: string, code: string, newPassword: string): Promise<void>;

    /**
     * Start employee SSO via the Cognito Hosted UI (Midway OIDC). Redirects the
     * browser; the session is picked up on return. No-op if SSO isn't enabled.
     */
    signInWithSso(): Promise<void>;

    /**
     * Sign out the current session.
     */
    signOut(): Promise<void>;

    /**
     * The currently authenticated user, or null if signed out.
     */
    getCurrentUser(): Promise<SupplierUser | null>;

    /**
     * Fetch the signed-in supplier's own M2M API credentials (clientId +
     * clientSecret) on demand. Called only when the user explicitly reveals the
     * secret in the Integration Guide; the secret is never fetched eagerly.
     * Throws if no credentials are provisioned yet (not approved).
     */
    getMyCredentials(): Promise<{ clientId: string; clientSecret: string }>;
}

export interface UploadService {
    /**
     * Upload a file on behalf of the signed-in supplier. Uploads land under a
     * per-supplier key prefix in a private S3 bucket.
     */
    uploadFile(file: File, onProgress?: (percent: number) => void): Promise<void>;
}

/**
 * UI engagement events reported to the backend (POST /v1/events), which emits
 * them as CloudWatch business metrics. The name must be one the service's
 * allow-list accepts (keep in lockstep with the events handler).
 */
export type AnalyticsEvent = 'integration_guide_opened' | 'asn_upload_file_selected';

export interface AnalyticsService {
    /**
     * Report a UI engagement event. Fire-and-forget: never throws and never
     * blocks the user action it accompanies (failures are swallowed, since a
     * dropped metric must not break the feature).
     */
    track(event: AnalyticsEvent): void;
}

/** A supplier item and its editable lead-time attributes. */
export interface LeadTimeItem {
    item: string;
    desc: string;
    /** Current lead time in days. */
    current: number;
    /** ISO country code (read-only). */
    country: string;
    endOfSale: string;
    endOfLife: string;
    hazardClass: string;
    dgGoods: boolean;
}

/** One audit-history entry for a lead-time item (fields blank if unchanged). */
export interface LeadTimeHistoryEntry {
    when: string;
    leadTime: string;
    eos: string;
    eol: string;
    hazard: string;
    by: string;
}

/** A single item's attribute change to persist. */
export interface LeadTimeChange {
    item: string;
    leadTime?: number | null;
    endOfSale?: string;
    endOfLife?: string;
    hazardClass?: string;
    dgGoods?: boolean;
}

export interface LeadTimeService {
    /** Supplier item catalog with current attribute values. */
    listItems(): Promise<LeadTimeItem[]>;
    /** Audit history for a single item. */
    getHistory(item: string): Promise<LeadTimeHistoryEntry[]>;
    /** Persist a batch of attribute changes. */
    saveChanges(changes: LeadTimeChange[]): Promise<void>;
}

// ── Supplier Control (internal access management) ─────────────────────────────

/** A supplier site (physical location) under a supplier organization. */
export interface ControlSite {
    id: string;
    /** Business site code leadership recognizes (e.g. DEN001). */
    code: string;
    name: string;
    location: string;
}

/** A supplier login and which site ids it can access. */
export interface ControlLogin {
    email: string;
    /** Map of siteId → granted. */
    access: Record<string, boolean>;
}

/** A supplier organization with its sites and logins. */
export interface ControlSupplier {
    id: string;
    name: string;
    sites: ControlSite[];
    logins: ControlLogin[];
}

/**
 * An approved (onboarded) supplier account, derived from Cognito: a native
 * (non-federated) account that is CONFIRMED + enabled. The counterpart to a
 * pending ControlRequest.
 */
export interface OnboardedSupplier {
    /** Cognito username (opaque id). */
    id: string;
    email: string;
    /** Supplier organization/company label. */
    company: string;
    /** Sites available for the supplier (0 until the sites model exists). */
    sites: number;
    /** Onboarding time (account creation, ISO-8601). */
    onboarded: string;
    /** Cognito account status (e.g. CONFIRMED). */
    status: string;
    /** The brand (team) this master owns; used to drill into its members. */
    supplierId?: string;
}

/** A legal entity reference a member can access (in the supplier drill-down). */
export interface SupplierEntityRef {
    supplierNo: string;
    displayLabel: string;
    category: string;
}

/** A selectable brand for the approval picker (with its legal-entity count). */
export interface SupplierOption {
    supplierId: string;
    supplierName: string;
    domainId: string;
    entityCount: number;
    /** True when this supplier already has a master (only one master allowed). */
    hasMaster?: boolean;
}

/** The supplier list plus an advisory suggested supplier for a request's email. */
export interface SupplierList {
    suppliers: SupplierOption[];
    /** Best-match supplierId derived from the request email, or null. Advisory. */
    suggestedSupplierId: string | null;
}

/** A member of a brand, with the legal entities they can access. */
export interface SupplierMemberAccess {
    username: string;
    email: string;
    role: 'MASTER' | 'MEMBER';
    enabled: boolean;
    /** Entities this member can act on (all supplier entities for an admin). */
    entities: SupplierEntityRef[];
}

/** A brand's members + per-member entity access (admin drill-down). */
export interface SupplierDetail {
    supplierId: string;
    /** Total legal entities owned by the brand. */
    entityCount: number;
    members: SupplierMemberAccess[];
}

/** A pending supplier-login access request awaiting approval. */
export interface ControlRequest {
    id: string;
    email: string;
    supplier: string;
    /** Number of sites available for the supplier. */
    sites: number;
    submitted: string;
    /** True once the requester has verified their email. */
    verified: boolean;
    /**
     * Optional, non-authoritative team the registrant asked to join at sign-up.
     * A suggestion for the approver; they confirm the real team in the picker.
     */
    teamHint?: string;
}

/** How a user authenticates — for the ops Users view. */
export type UserType = 'supplier' | 'employee';

/** A user pool account, classified by type for ops visibility. */
export interface ControlUser {
    username: string;
    email: string;
    /** supplier = password sign-up; employee = federated (Midway) SSO. */
    type: UserType;
    /** Role groups (employees only); empty for suppliers. */
    roles: string[];
    enabled: boolean;
    status: string;
    created: string;
    /** Current team (credential + reporting identity); empty for solo/employee. */
    supplierId?: string;
    /** Email domain (the team-picker grouping key); empty when unknown. */
    domain?: string;
    /**
     * Brand role for a supplier login: 'MASTER' | 'MEMBER'. Empty for employees
     * and for supplier logins with no role stamped yet (treated as a member).
     */
    role?: string;
}

/** How the admin assigns a supplier at approval: pick an existing one or name a new one. */
export interface SupplierAssignment {
    /** Existing supplier to join. */
    supplierId?: string;
    /** New supplier to create under the login's domain. */
    supplierName?: string;
    /**
     * Role to grant the login at the supplier. Omit to default to MASTER (the
     * supplier's owning login). Pass 'MEMBER' when attaching a login to a
     * supplier that already has an admin.
     */
    role?: 'MASTER' | 'MEMBER';
}

export interface SupplierControlService {
    /** All supplier organizations (with sites + logins). */
    listSuppliers(): Promise<ControlSupplier[]>;
    /** Approved (onboarded) supplier accounts (Cognito-derived directory). */
    listOnboardedSuppliers(): Promise<OnboardedSupplier[]>;
    /** Pending supplier sign-ups awaiting approval. */
    listRequests(): Promise<ControlRequest[]>;
    /**
     * Approve a pending supplier sign-up (enable the account + provision/reuse
     * its supplier's M2M client). Pass a `SupplierAssignment` to attach the
     * login to an existing supplier (supplierId) or create and join a new one
     * (supplierName) so it shares that supplier's credential and reporting
     * numbers; omit it to make the login its own supplier. Returns the refreshed
     * pending list.
     */
    approveRequest(requestId: string, assignment?: SupplierAssignment): Promise<ControlRequest[]>;
    /**
     * Move an already-approved login to a different supplier (existing supplierId
     * or a new supplierName created under the login's domain). The login
     * immediately resolves to the new supplier's shared credential and reporting
     * partition.
     */
    reassignSupplier(username: string, assignment: SupplierAssignment): Promise<void>;
    /** Grant or revoke a login's access to a site. */
    setSiteAccess(email: string, siteId: string, granted: boolean): Promise<void>;
    /** All user-pool accounts, classified as supplier vs employee (ops view). */
    listUsers(): Promise<ControlUser[]>;
    /**
     * Drill into a brand (by its master's supplierId): its members and, per member,
     * the legal entities they can access. A MASTER covers all brand entities.
     */
    getSupplierDetail(supplierId: string): Promise<SupplierDetail>;
    /**
     * List all brands for the approval picker. Pass the request's email to get
     * an advisory `suggestedSupplierId` (a clue; the admin always confirms).
     */
    listSuppliersForPicker(email?: string): Promise<SupplierList>;
    /** A brand's legal entities (to show scope when a brand is selected). */
    getSupplierEntities(supplierId: string): Promise<SupplierEntity[]>;
}

// ── Master (brand self-service teammate management) ───────────────────────────

/** A teammate on the signed-in master's brand. */
export interface SupplierMember {
    /** Cognito username (opaque id). */
    username: string;
    email: string;
    /** 'MASTER' | 'MEMBER'. */
    role: 'MASTER' | 'MEMBER';
    /** Whether the account is enabled (disabled teammates can't sign in). */
    enabled: boolean;
    /** Cognito account status (e.g. CONFIRMED, FORCE_CHANGE_PASSWORD). */
    status: string;
}

/** A legal entity (billing entity) under the brand — the grant grain. */
export interface SupplierEntity {
    /** Stable SupplierNo (the authorization + PO join key). */
    supplierNo: string;
    /** Raw composite label from the source sheet (for display). */
    displayLabel: string;
    /** Parsed AP group (e.g. "CORPORATE AP"). */
    apGroup: string;
    /** Parsed category: 'GOODS' | 'SERVICES' | ''. */
    category: string;
    /** PO count (denormalized convenience). */
    poCount: number;
}

/**
 * Brand self-service user management, available to a MASTER. The brand (team),
 * the caller's role, and the brand domain are all resolved server-side from the
 * token — the UI never passes them. Members share the brand's one API
 * credential, so inviting one issues no new credential.
 */
export interface SupplierAdminService {
    /** List the signed-in master's brand members (role + status). */
    listMembers(): Promise<SupplierMember[]>;
    /**
     * Invite a teammate as a MEMBER. The invitee's email domain must match the
     * brand's domain (enforced server-side). Cognito emails an invitation with a
     * temporary password. Returns the created member.
     */
    inviteMember(email: string): Promise<SupplierMember>;
    /** Disable a teammate (scoped to the brand). */
    disableMember(username: string): Promise<void>;
    /** Re-enable a previously disabled teammate. */
    enableMember(username: string): Promise<void>;
    /** List the brand's legal entities (the grant vocabulary). */
    listEntities(): Promise<SupplierEntity[]>;
    /** The SupplierNos a member is currently granted ('Y'). */
    getMemberGrants(username: string): Promise<string[]>;
    /**
     * Set a member's grants. `grants` maps SupplierNo -> allowed; only entities
     * belonging to the brand are accepted (enforced server-side). Returns the
     * member's resulting granted SupplierNos.
     */
    setMemberGrants(username: string, grants: Record<string, boolean>): Promise<string[]>;
}

// ── Supplier Insights (internal reporting) ────────────────────────────────────

/** A supplier performance row for the Insights supplier directory. */
export interface InsightsSupplier {
    id: string;
    name: string;
    asns: number;
    /** Validation pass %. */
    valid: number;
    /** Downstream pass %. */
    dsOk: number;
    /** Compliance % (validation AND downstream). */
    compliance: number;
    last: string;
    status: 'Active' | 'Inactive';
}

/** Aggregate metrics for the Insights overview tiles. */
export interface InsightsOverview {
    supplierCount: number;
    asnCount: number;
    onTimePct: number;
    errorRate: number;
}

/** ASN outcome for the ASN-review table. */
export type InsightsOutcome = 'passed' | 'schema' | 'semantic' | 'business';

/** One ASN transaction row in the ASN-review table. */
export interface InsightsTransaction {
    asn: string;
    supplier: string;
    pos: string[];
    date: string;
    outcome: InsightsOutcome;
    error: string;
    lines: number;
    attempts: number;
}

/** A purchase order row for the PO-review table. */
export interface InsightsPurchaseOrder {
    po: string;
    supplier: string;
    lines: number;
    needBy: string;
    status: string;
}

/** A PO-line row for the PO-lines-review table. */
export interface InsightsPoLine {
    po: string;
    line: number;
    item: string;
    supplier: string;
    ordered: number;
    received: number;
    needBy: string;
}

export interface SupplierInsightsService {
    /** Top-line overview metrics. */
    getOverview(): Promise<InsightsOverview>;
    /** Per-supplier performance rows. */
    listSuppliers(): Promise<InsightsSupplier[]>;
    /** ASN transactions across suppliers. */
    listTransactions(): Promise<InsightsTransaction[]>;
    /** Purchase orders. */
    listPurchaseOrders(): Promise<InsightsPurchaseOrder[]>;
    /** PO lines. */
    listPoLines(): Promise<InsightsPoLine[]>;
}
